import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Users, Plus, Edit, Key, UserCheck, UserX, Search,
  CheckSquare, Square, Calendar, Mail, Phone, Shield,
  Briefcase, User, Truck, Settings, Clock, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBusiness } from '../../contexts/BusinessContext';
import { fieldStaffAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import AddEditFieldStaffModal from '../../components/modals/AddEditFieldStaffModal';
import ListPageHeader from '../../components/shared/ListPageHeader';

// Role template display metadata
const ROLE_BADGES = {
  sales_rep: { label: 'Sales Rep', icon: Briefcase, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  cashier: { label: 'Cashier', icon: User, bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  delivery: { label: 'Delivery', icon: Truck, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  custom: { label: 'Custom', icon: Settings, bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' }
};

const UsersRolesPage = () => {
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [fieldStaff, setFieldStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchFieldStaff();
    }
  }, [activeBusiness, statusFilter]);

  const fetchFieldStaff = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const { data } = await fieldStaffAPI.list(params);
      setFieldStaff(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff'));
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = fieldStaff.filter(staff => {
    const matchesSearch =
      staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.mobile?.includes(searchTerm) ||
      (staff.email && staff.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (staff.employee_code && staff.employee_code.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = roleFilter === 'all' || staff.role_template === roleFilter;

    return matchesSearch && matchesRole;
  });

  const handleAddStaff = () => {
    setSelectedStaff(null);
    setShowModal(true);
  };

  const handleEditStaff = (staff) => {
    setSelectedStaff(staff);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedStaff(null);
  };

  const handleSuccess = () => {
    fetchFieldStaff();
    handleModalClose();
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredStaff.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredStaff.map(s => s.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one field staff');
      return;
    }

    const label = action === 'activate' ? 'Activate' : 'Deactivate';
    setConfirmModal({
      title: `${label} ${selectedIds.length} Staff`,
      description: `Are you sure you want to ${label.toLowerCase()} ${selectedIds.length} selected staff member(s)?`,
      confirmLabel: label,
      confirmClass: action === 'activate' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700',
      icon: action === 'activate' ? UserCheck : UserX,
      iconBgClass: action === 'activate' ? 'bg-emerald-100' : 'bg-slate-100',
      iconClass: action === 'activate' ? 'text-emerald-600' : 'text-slate-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const { data } = await fieldStaffAPI.bulkAction({ staff_ids: selectedIds, action });
          toast.success(data.message);
          setSelectedIds([]);
          fetchFieldStaff();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error performing bulk action'));
        }
      },
    });
  };

  const handleToggleStatus = (staff) => {
    const isActive = staff.status === 'active';
    const newStatus = isActive ? 'inactive' : 'active';
    const label = isActive ? 'Deactivate' : 'Activate';

    setConfirmModal({
      title: `${label} Staff`,
      description: `Are you sure you want to ${label.toLowerCase()} ${staff.full_name}?`,
      confirmLabel: label,
      confirmClass: isActive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700',
      icon: isActive ? UserX : UserCheck,
      iconBgClass: isActive ? 'bg-amber-100' : 'bg-emerald-100',
      iconClass: isActive ? 'text-amber-600' : 'text-emerald-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await fieldStaffAPI.updateStatus(staff.id, { user_id: staff.id, status: newStatus });
          toast.success(`${staff.full_name} ${label.toLowerCase()}d successfully`);
          fetchFieldStaff();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error updating status'));
        }
      },
    });
  };

  const handleResetPassword = (staff) => {
    setConfirmModal({
      title: 'Reset Password',
      description: `Reset password for ${staff.full_name}? A new temporary password will be generated.`,
      confirmLabel: 'Reset Password',
      confirmClass: 'bg-amber-600 hover:bg-amber-700',
      icon: Key,
      iconBgClass: 'bg-amber-100',
      iconClass: 'text-amber-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const { data } = await fieldStaffAPI.resetPassword(staff.id);
          if (data.temp_password) {
            toast.success(`Password reset! Temp password: ${data.temp_password} -- share securely.`);
          } else {
            toast.success('Password reset successfully!');
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error resetting password'));
        }
      },
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const getRoleBadge = (roleTemplate) => {
    const badge = ROLE_BADGES[roleTemplate] || ROLE_BADGES.custom;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.bg} ${badge.text} border ${badge.border}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const activeCount = filteredStaff.filter(s => s.status === 'active').length;
  const inactiveCount = filteredStaff.filter(s => s.status !== 'active').length;
  const salesRepCount = fieldStaff.filter(s => s.role_template === 'sales_rep').length;
  const cashierCount = fieldStaff.filter(s => s.role_template === 'cashier').length;

  return (
    <div className="h-full flex flex-col" data-testid="users-roles-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Users & Roles"
        subtitle="Manage staff members and their permissions"
        stats={[
          { icon: Users, label: 'Total Staff', value: fieldStaff.length, color: 'slate' },
          { icon: UserCheck, label: 'Active', value: activeCount, color: 'emerald' },
          { icon: Briefcase, label: 'Sales Reps', value: salesRepCount, color: 'blue' },
          { icon: User, label: 'Cashiers', value: cashierCount, color: 'purple' },
        ]}
        ctaLabel="Add Staff Member"
        ctaIcon={Plus}
        onCtaClick={handleAddStaff}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, mobile, email or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                data-testid="staff-search-input"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  statusFilter === 'all' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1 ${
                  statusFilter === 'active' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                }`}
              >
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Active
              </button>
              <button
                onClick={() => setStatusFilter('inactive')}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1 ${
                  statusFilter === 'inactive' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                }`}
              >
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span> Inactive
              </button>
            </div>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              data-testid="staff-role-filter"
            >
              <option value="all">All Roles</option>
              <option value="sales_rep">Sales Rep</option>
              <option value="cashier">Cashier</option>
              <option value="delivery">Delivery</option>
              <option value="custom">Custom</option>
            </select>

            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-[12px] font-medium text-blue-700">{selectedIds.length} selected</p>
                <button
                  onClick={() => handleBulkAction('activate')}
                  className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px] font-medium hover:bg-emerald-700"
                >
                  Activate
                </button>
                <button
                  onClick={() => handleBulkAction('deactivate')}
                  className="px-2 py-1 bg-slate-600 text-white rounded text-[10px] font-medium hover:bg-slate-700"
                >
                  Deactivate
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading staff...</p>
              </div>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchTerm || roleFilter !== 'all' ? 'No staff found' : 'No staff members yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1">
                {searchTerm || roleFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first staff member to get started'}
              </p>
              {!searchTerm && roleFilter === 'all' && (
                <button
                  onClick={handleAddStaff}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium"
                  data-testid="add-first-staff-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add First Staff Member
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal">
                        <button
                          onClick={toggleSelectAll}
                          className="flex items-center justify-center"
                          data-testid="select-all-staff"
                        >
                          {selectedIds.length === filteredStaff.length && filteredStaff.length > 0 ? (
                            <CheckSquare className="w-4 h-4 text-teal-600" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                      </th>
                      <th className="tl-th tl-th-teal">Staff Member</th>
                      <th className="tl-th tl-th-teal">Role</th>
                      <th className="tl-th tl-th-teal">Contact</th>
                      <th className="tl-th tl-th-teal text-center">Status</th>
                      <th className="tl-th tl-th-teal">Last Login</th>
                      <th className="tl-th tl-th-teal">Joined</th>
                      <th className="tl-th tl-th-teal"></th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredStaff.map((staff, idx) => {
                      const isActive = staff.status === 'active';
                      return (
                        <tr
                          key={staff.id}
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                          data-testid={`staff-row-${staff.id}`}
                        >
                          <td className="tl-td">
                            <button
                              onClick={() => toggleSelect(staff.id)}
                              className="flex items-center justify-center"
                            >
                              {selectedIds.includes(staff.id) ? (
                                <CheckSquare className="w-4 h-4 text-teal-600" />
                              ) : (
                                <Square className="w-4 h-4 text-slate-400" />
                              )}
                            </button>
                          </td>
                          <td className="tl-td">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${getInitialColor(staff.full_name)}`}>
                                {getInitials(staff.full_name)}
                              </div>
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold text-slate-800 truncate">{staff.full_name}</p>
                                {staff.employee_code && (
                                  <p className="text-[10px] text-slate-400 font-mono">{staff.employee_code}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="tl-td">
                            {getRoleBadge(staff.role_template)}
                          </td>
                          <td className="tl-td">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
                                <Phone className="w-3 h-3 text-slate-400" />
                                {staff.mobile}
                              </div>
                              {staff.email && (
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 truncate max-w-[180px]">
                                  <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <span className="truncate">{staff.email}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="tl-td text-center">
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-medium">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-medium">
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="tl-td">
                            <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {staff.last_login ? (
                                new Date(staff.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                              ) : (
                                <span className="text-slate-400">Never</span>
                              )}
                            </div>
                          </td>
                          <td className="tl-td">
                            <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {new Date(staff.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                            </div>
                          </td>
                          <td className="tl-td">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => handleEditStaff(staff)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResetPassword(staff)}>
                                  <Key className="w-4 h-4 mr-2" /> Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleToggleStatus(staff)}>
                                  {isActive ? (
                                    <><UserX className="w-4 h-4 mr-2" /> Deactivate</>
                                  ) : (
                                    <><UserCheck className="w-4 h-4 mr-2" /> Activate</>
                                  )}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredStaff.length}</span> of <span className="font-medium text-slate-700">{fieldStaff.length}</span> staff members
                </p>
                <p className="text-[11px] text-slate-400">Staff can login at /staff-login</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <AddEditFieldStaffModal
          isOpen={showModal}
          onClose={handleModalClose}
          staff={selectedStaff}
          onSuccess={handleSuccess}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default UsersRolesPage;
